"use client";

import { useMemo, useState } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import {
    DELAI_MISE_EN_DEMEURE_JOURS,
    PRESCRIPTION_ANS,
    TAUX,
    TAUX_PLANCHER,
    TAUX_SUPPLETIF,
    composerMiseEnDemeure,
    concerne,
    decompte,
    euros,
    joursEntre,
    montant,
    pourcent,
    prescription,
    recapitulatif,
    valider,
} from "@/lib/tools/unpaidInvoice";
import { dateEnToutesLettres, documentEnTexte, nomDuFichier } from "@/lib/tools/documentText";
import { telechargerDocument } from "@/lib/tools/documentPdf";
import { ApercuDocument } from "./DocumentPaper";
import { PadSignature, useSignature } from "./SignaturePad";
import {
    Avancer,
    Champ,
    Choix,
    Ecran,
    Liste,
    Recommencer,
    Resultat,
    Segments,
    remplir,
    useParcours,
} from "./Journey";
import { useDossierOutil, useSignalerIdentite } from "./ToolsDossier";

/**
 * La facture impayée : ce qui est dû, et la lettre pour le réclamer.
 *
 * TOUT SE JOUE SUR LA QUALITÉ DU CLIENT. Entre professionnels, les pénalités
 * courent seules depuis l'échéance et 40 € s'y ajoutent ; avec un particulier,
 * rien ne court avant la lettre, et réclamer ces 40 € serait faux. Le parcours
 * pose donc cette question en premier et ne montre jamais les mêmes écrans.
 *
 * LE PREMIER REFUS EST LA PRESCRIPTION, deux ans face à un consommateur, cinq
 * entre professionnels : une lettre envoyée après ce délai s'expose à une fin de
 * non-recevoir, et l'outil le dit plutôt que de l'écrire. Ce délai court de la
 * FIN DE LA PRESTATION, pas de l'échéance : c'est la seule raison pour laquelle
 * le parcours demande cette date, sur un écran à part.
 */

const L = {
    fr: {
        phases: ["Votre facture", "Les parties", "Votre lettre"],
        retour: "Retour",
        prive: "Tout se fait sur votre appareil : la lettre s'y fabrique, personne ne reçoit vos réponses, pas même nous, et tout disparaît avec l'onglet.",

        clientTitre: "Qui vous doit de l'argent ?",
        clientAide: "Tout part de là : les pénalités de retard et l'indemnité de 40 € n'existent qu'entre professionnels.",
        clientPro: "Une entreprise",
        clientProDetail: "Société, association, administration",
        clientParticulier: "Un particulier",
        clientParticulierDetail: "Un consommateur, pour ses besoins privés",

        factureTitre: "La facture impayée",
        factureAide: "Reprenez-la telle qu'elle a été émise : c'est elle que la lettre va désigner.",
        champNumero: "Numéro de facture",
        champObjet: "Ce qu'elle facture",
        aideObjet: "En quelques mots, comme sur la facture : « la création de votre identité visuelle ».",
        champMontant: "Montant restant dû",
        aideMontant: "Le montant que vous réclamez, tel qu'il figure sur la facture.",
        champDateFacture: "Date de la facture",
        champEcheance: "Date d'échéance",
        aideEcheance: "La date à laquelle le règlement était attendu. Le retard commence le lendemain.",

        prestationTitre: "Quand avez-vous terminé la prestation ?",
        prestationAide: "Le délai pour agir en justice court en principe de ce jour-là, et non de la date de la facture ni de son échéance : facturer tard ne le rallonge pas.",
        champFinPrestation: "Fin de la prestation ou livraison",
        aideFinPrestation: "Le jour où vous avez terminé le travail ou livré ce que la facture couvre. Si votre contrat prévoit autre chose, c'est lui qui compte.",

        horsSujetTitre: "Il n'y a rien à réclamer aujourd'hui",
        refusPasEncoreEchue: "Cette facture n'est pas encore arrivée à échéance : rien n'est exigible, et aucune pénalité ne court. Une relance amiable reste possible et souvent suffisante, mais une mise en demeure envoyée avant l'échéance n'aurait aucun effet. Revenez le lendemain de la date de règlement prévue.",
        refusPrescritPro: "Plus de cinq ans se sont écoulés depuis la fin de votre prestation, le jour d'où court le délai pour agir. L'action est prescrite en application de l'article L. 110-4 du code de commerce, ou de l'article 2224 du code civil si aucun de vous deux n'est commerçant : votre client peut opposer la prescription et refuser de payer sans autre motif, sauf si ce délai a été interrompu entre-temps, par une action en justice ou une reconnaissance écrite de sa dette. Une lettre reste possible, mais elle n'a plus de portée juridique.",
        refusPrescritParticulier: "Plus de deux ans se sont écoulés depuis la fin de votre prestation, le jour d'où court le délai pour agir. L'action d'un professionnel contre un consommateur se prescrit par deux ans, en application de l'article L. 218-2 du code de la consommation, et ce délai ne peut être allongé, même par vos conditions générales. Sauf s'il a été interrompu entre-temps, par une action en justice ou une reconnaissance écrite de la dette, votre client peut opposer la prescription : la créance existe toujours, mais elle n'est plus recouvrable en justice.",

        tauxTitre: "Vos conditions de vente prévoient-elles un taux ?",
        tauxAide: "Vos conditions générales ou vos factures mentionnent presque toujours un taux de pénalités. À défaut, la loi en applique un.",
        champTauxPrevu: "Un taux est prévu",
        oui: "Oui",
        non: "Non",
        champTaux: "Taux annuel prévu",
        aideTaux: "En pourcentage. Il ne peut pas être inférieur à {plancher}, soit trois fois le taux légal.",
        tauxSuppletif: "Sans clause, la loi applique le taux directeur de la Banque centrale européenne majoré de dix points, soit {taux} pour ce semestre.",
        tauxRemonte: "Le taux que vous indiquez est inférieur au minimum légal : c'est {plancher} qui s'appliquera.",

        partiesTitre: "Qui écrit, et à qui",
        partiesAide: "Ces coordonnées figurent en tête de la lettre. Écrivez l'adresse comme sur une enveloppe.",
        champPrenoms: "Vos prénoms",
        champNom: "Votre nom",
        champAdresse: "Votre adresse",
        aideAdresse: "Rue, puis code postal et commune, sur des lignes séparées.",
        champDestinataire: "Nom du débiteur",
        aideDestinataire: "La dénomination exacte de l'entreprise, ou le nom de la personne.",
        champAdresseDestinataire: "Son adresse",

        signatureTitre: "Où et quand signez-vous ?",
        signatureAide: "La lettre part en recommandé avec avis de réception : l'avis prouvera le jour où votre client l'a reçue, et elle ne produit ses effets qu'à partir de ce jour-là.",
        champLieuSignature: "Lieu de signature",
        champDateDeSignature: "Date de la lettre",
        signatureDessin: "Votre signature",
        signatureDessinAide: "Facultative. Tracez-la à la souris ou au doigt, ou laissez l'emplacement vide pour signer à la main après impression. Elle sera reprise par vos autres documents de la visite.",
        signatureReprise: "Reprise de votre document précédent. Retracez-la par-dessus ou effacez-la si ce n'est pas la bonne.",
        effacer: "Effacer",

        continuer: "Continuer",
        voirDocument: "Voir ma lettre",
        resultatTitre: "Votre mise en demeure",
        decompteTitre: "Ce que vous pouvez réclamer",
        recapTitre: "Le détail",
        apercu: "Aperçu de la lettre",
        telecharger: "Télécharger en PDF",
        copier: "Copier le texte",
        copie: "Texte copié",
        recommencer: "Recommencer",

        manquesTitre: "Il manque encore quelque chose",
        manquesAide: "Complétez les champs signalés pour obtenir votre lettre.",
        verifierTitre: "Une valeur à vérifier",
        verifierAide: "Ces champs sont remplis, mais il y a un problème dans ce qu'ils contiennent. Corrigez-les, puis revenez ici.",
        erreurFormat: "Ce format n'est pas reconnu.",
        erreurMontant: "Indiquez un montant en euros, par exemple 1250,50.",
        erreurEcheanceAvant: "L'échéance ne peut pas précéder la date de la facture.",
        alertePrescriptionProche: "La prescription approche : vous avez jusqu'au {date} pour agir, {ans} ans après la fin de votre prestation. Envoyez la lettre sans attendre, et sachez qu'elle n'arrête pas ce délai : une action en justice ou une reconnaissance de la dette par votre client l'interrompent.",
        alerteTauxSousPlancher: "Le taux de vos conditions générales est inférieur au minimum légal. Nous calculons avec {plancher}, le taux applicable malgré la clause.",

        interetsACourirTitre: "Ce que la lettre déclenche",
        interetsACourir: "Face à un particulier, aucune pénalité n'a couru : le code de commerce ne s'applique qu'entre professionnels, et il ne doit pas l'indemnité de 40 €. Par défaut, c'est cette lettre qui fera courir les intérêts au taux légal, soit {taux} l'an, à compter de sa réception : elle ne réclame donc que le principal. Si votre devis ou vos conditions générales prévoient que la seule échéance vaut mise en demeure, ces intérêts courent déjà depuis l'échéance, mais cette lettre ne les chiffre pas.",

        suiteTitre: "Et maintenant",
        suite1: "Envoyez la lettre en recommandé avec avis de réception : le délai de {delai} jours court de la date de réception, et l'avis la prouve.",
        suite2: "Gardez une copie de la lettre, de l'avis de réception et de la facture : ce sont les trois pièces d'un dossier d'injonction de payer.",
        suite3: "Passé le délai, la requête en injonction de payer se dépose sans avocat : au tribunal de commerce si la dette naît d'un acte de commerce entre commerçants, au tribunal judiciaire sinon, notamment si votre client est un particulier ou exerce une profession libérale. S'il s'y oppose devant le tribunal judiciaire pour plus de 10 000 €, l'avocat devient obligatoire.",
        suite4: "Une mise en demeure n'interrompt pas la prescription. Une action en justice l'interrompt, tout comme une reconnaissance de la dette par votre client ou une mesure conservatoire : si le délai approche, ne comptez pas sur la lettre pour le retenir.",

        fondTitre: "Pourquoi les 40 € comptent",
        fond: "L'indemnité forfaitaire pour frais de recouvrement est due de plein droit dès le premier jour de retard entre professionnels, sans que vous ayez à la demander. Elle s'ajoute aux pénalités et se compte par facture : dix factures réglées en retard dans l'année, ce sont 400 € que vous pouvez réclamer en plus des pénalités.",
    },
    en: {
        phases: ["Your invoice", "The parties", "Your letter"],
        retour: "Back",
        prive: "Everything happens on your device: the letter is built there, nobody receives your answers, not even us, and it all disappears with the tab.",

        clientTitre: "Who owes you money?",
        clientAide: "This is the question that decides everything: late payment penalties and the €40 fee only exist between businesses.",
        clientPro: "A business",
        clientProDetail: "Company, association, public body",
        clientParticulier: "An individual",
        clientParticulierDetail: "A consumer, for private purposes",

        factureTitre: "The unpaid invoice",
        factureAide: "Copy it as it was issued: this is what the letter will refer to.",
        champNumero: "Invoice number",
        champObjet: "What it covers",
        aideObjet: "In a few words, as on the invoice: « creating your visual identity ».",
        champMontant: "Amount still due",
        aideMontant: "The amount you are claiming, as it appears on the invoice.",
        champDateFacture: "Invoice date",
        champEcheance: "Due date",
        aideEcheance: "The date payment was expected. The delay starts the next day.",

        prestationTitre: "When did you finish the work?",
        prestationAide: "The time limit for going to court normally runs from that day, not from the invoice date or its due date: invoicing late does not extend it.",
        champFinPrestation: "Work completed or goods delivered",
        aideFinPrestation: "The day you finished the work or delivered what the invoice covers. If your contract provides otherwise, the contract prevails.",

        horsSujetTitre: "There is nothing to claim today",
        refusPasEncoreEchue: "This invoice is not due yet: nothing is payable, and no penalty is running. A friendly reminder is still possible and often enough, but a formal demand sent before the due date would have no effect. Come back the day after the agreed payment date.",
        refusPrescritPro: "More than five years have passed since you finished the work, the day the time limit runs from. The claim is time-barred under article L. 110-4 of the commercial code, or article 2224 of the civil code if neither of you is a trader: your client can rely on that and refuse to pay without any other reason, unless the period was interrupted in the meantime, by court action or a written acknowledgement of the debt. A letter is still possible, but it no longer carries legal weight.",
        refusPrescritParticulier: "More than two years have passed since you finished the work, the day the time limit runs from. A professional's claim against a consumer is time-barred after two years, under article L. 218-2 of the consumer code, and that period cannot be extended, not even by your terms and conditions. Unless it was interrupted in the meantime, by court action or a written acknowledgement of the debt, your client can rely on it: the debt still exists, but it can no longer be enforced in court.",

        tauxTitre: "Do your terms of sale set a rate?",
        tauxAide: "Your terms and conditions or your invoices almost always mention a penalty rate. Failing that, the law applies one.",
        champTauxPrevu: "A rate is set",
        oui: "Yes",
        non: "No",
        champTaux: "Annual rate set",
        aideTaux: "As a percentage. It cannot be lower than {plancher}, three times the statutory rate.",
        tauxSuppletif: "With no clause, the law applies the European Central Bank's main rate plus ten points, that is {taux} for this half-year.",
        tauxRemonte: "The rate you entered is below the legal minimum: {plancher} will apply instead.",

        partiesTitre: "Who is writing, and to whom",
        partiesAide: "These details head the letter. Write the address as on an envelope.",
        champPrenoms: "Your forenames",
        champNom: "Your surname",
        champAdresse: "Your address",
        aideAdresse: "Street, then postcode and town, on separate lines.",
        champDestinataire: "Debtor's name",
        aideDestinataire: "The exact company name, or the person's name.",
        champAdresseDestinataire: "Their address",

        signatureTitre: "Where and when are you signing?",
        signatureAide: "The letter goes by registered post with acknowledgement of receipt: the acknowledgement will prove the day your client received it, and the letter only takes effect from that day.",
        champLieuSignature: "Place of signature",
        champDateDeSignature: "Date of the letter",
        signatureDessin: "Your signature",
        signatureDessinAide: "Optional. Draw it with the mouse or your finger, or leave the space empty and sign by hand after printing. Your other documents in this visit will reuse it.",
        signatureReprise: "Taken from your previous document. Draw over it, or clear it if it is not the right one.",
        effacer: "Clear",

        continuer: "Continue",
        voirDocument: "See my letter",
        resultatTitre: "Your formal demand",
        decompteTitre: "What you can claim",
        recapTitre: "The detail",
        apercu: "Letter preview",
        telecharger: "Download as PDF",
        copier: "Copy the text",
        copie: "Text copied",
        recommencer: "Start again",

        manquesTitre: "Something is still missing",
        manquesAide: "Fill in the highlighted fields to get your letter.",
        verifierTitre: "Something to check",
        verifierAide: "These fields are filled in, but there is a problem with what they contain. Correct them, then come back here.",
        erreurFormat: "This format is not recognised.",
        erreurMontant: "Enter an amount in euros, for example 1250.50.",
        erreurEcheanceAvant: "The due date cannot come before the invoice date.",
        alertePrescriptionProche: "The limitation period is closing in: you have until {date} to act, {ans} years after you finished the work. Send the letter without waiting, and bear in mind it does not stop that clock: court action or an acknowledgement of the debt by your client interrupts it.",
        alerteTauxSousPlancher: "The rate in your terms is below the legal minimum. We calculate with {plancher}, the rate that applies despite the clause.",

        interetsACourirTitre: "What this letter sets off",
        interetsACourir: "With an individual, no penalty has been running: the commercial code only applies between businesses, and the €40 fee is not owed by them. By default, it is this letter that starts interest at the statutory rate, {taux} a year, from the day it is received: so it only claims the principal. If your quote or your terms say the due date alone serves as formal notice, that interest has been running since the due date, but this letter does not calculate it.",

        suiteTitre: "What happens next",
        suite1: "Send the letter by registered post with acknowledgement of receipt: the {delai}-day period runs from the date of receipt, which the acknowledgement proves.",
        suite2: "Keep a copy of the letter, the acknowledgement and the invoice: those are the three documents of a payment order application.",
        suite3: "Once the period has passed, the payment order application is filed without a lawyer: with the commercial court if the debt arises from a commercial transaction between traders, and with the judicial court otherwise, in particular if your client is an individual or a liberal professional. If they contest it before the judicial court for more than €10,000, a lawyer becomes compulsory.",
        suite4: "A formal demand does not interrupt the limitation period. Court action does, as does an acknowledgement of the debt by your client or a protective measure: if the deadline is close, do not count on the letter to hold it back.",

        fondTitre: "Why the €40 matter",
        fond: "The flat fee for recovery costs is owed as of right from the first day of delay between businesses, without you having to ask for it. It comes on top of the penalties and counts per invoice: ten invoices paid late in a year means €400 you can claim on top of them.",
    },
};

const LIBELLES_MANQUES = {
    fr: {
        client: "La qualité de votre client",
        prenoms: "Vos prénoms",
        nom: "Votre nom",
        adresse: "Votre adresse",
        destinataire: "Le nom du débiteur",
        adresseDestinataire: "L'adresse du débiteur",
        numeroFacture: "Le numéro de la facture",
        objet: "Ce que la facture couvre",
        montant: "Le montant réclamé",
        finPrestation: "La date de fin de la prestation",
        dateFacture: "La date de la facture",
        echeance: "La date d'échéance",
        tauxContrat: "Le taux prévu à vos conditions de vente",
        lieuDeSignature: "Le lieu de signature",
        dateDeSignature: "La date de la lettre",
    },
    en: {
        client: "Whether your client is a business",
        prenoms: "Your forenames",
        nom: "Your surname",
        adresse: "Your address",
        destinataire: "The debtor's name",
        adresseDestinataire: "The debtor's address",
        numeroFacture: "The invoice number",
        objet: "What the invoice covers",
        montant: "The amount claimed",
        finPrestation: "The date the work was completed",
        dateFacture: "The invoice date",
        echeance: "The due date",
        tauxContrat: "The rate set in your terms of sale",
        lieuDeSignature: "The place of signature",
        dateDeSignature: "The date of the letter",
    },
};

const PHASES_PAR_ECRAN = {
    client: 0,
    facture: 0,
    prestation: 0,
    horsSujet: 0,
    taux: 1,
    parties: 1,
    signature: 2,
    resultat: 2,
};

const VIDE = {
    client: "",
    numeroFacture: "",
    objet: "",
    montant: "",
    dateFacture: "",
    echeance: "",
    finPrestation: "",
    tauxPrevu: "",
    tauxContrat: "",
    prenoms: "",
    nom: "",
    adresse: "",
    destinataire: "",
    adresseDestinataire: "",
    lieuDeSignature: "",
    dateDeSignature: "",
};

/* Le dernier jour pour agir, écrit dans la langue du parcours. */
const dateLongue = (iso, lang) =>
    lang === "en"
        ? new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-GB", {
              day: "numeric",
              month: "long",
              year: "numeric",
              timeZone: "UTC",
          })
        : dateEnToutesLettres(iso);

export default function FactureImpayee({ lang = "fr", aujourdhui }) {
    const t = L[lang] || L.fr;
    const { identite } = useDossierOutil();
    const [r, setR] = useState(() => ({ ...VIDE, ...identite }));
    const [copie, setCopie] = useState(false);
    const { signature, poser: poserSignature } = useSignature();

    const { etape, sens, reduit, rang, aller, retour, recommencer, communs } = useParcours(
        "client",
        () => {
            setR({ ...VIDE, ...identite });
            setCopie(false);
        },
        PHASES_PAR_ECRAN,
        null,
        // Reprendre où l'on en était, le temps de l'onglet (Journey.jsx).
        { r, setR },
    );

    useSignalerIdentite({ prenoms: r.prenoms, nom: r.nom }, etape === "signature" || etape === "resultat");

    const poser = (cle) => (valeur) => setR((p) => ({ ...p, [cle]: valeur }));

    const jour = String(aujourdhui).slice(0, 10);
    const reponses = useMemo(
        () => ({ ...r, dateDeSignature: r.dateDeSignature || jour }),
        [r, jour],
    );

    const controle = useMemo(() => valider(reponses, aujourdhui), [reponses, aujourdhui]);
    const lettre = useMemo(() => composerMiseEnDemeure(reponses, aujourdhui), [reponses, aujourdhui]);
    const compte = useMemo(() => decompte(reponses, aujourdhui), [reponses, aujourdhui]);
    const recap = useMemo(() => recapitulatif(reponses, aujourdhui), [reponses, aujourdhui]);
    const delai = useMemo(() => prescription(reponses, aujourdhui), [reponses, aujourdhui]);

    const rempli = (...cles) => cles.every((c) => String(r[c] || "").trim());

    /* `valider` se tait tant que la facture n'est pas échue, puisqu'il n'y a
       alors rien à réclamer. L'écran de saisie, lui, doit signaler un montant
       illisible ou une échéance antérieure à la facture au moment où on les
       tape, sans attendre le résultat. */
    const soucis = useMemo(() => {
        const trouves = {};
        if (String(r.montant || "").trim() && montant(r.montant).motif) {
            trouves.montant = t.erreurMontant;
        }
        const ecart = joursEntre(r.dateFacture, r.echeance);
        if (ecart !== null && ecart < 0) trouves.echeance = t.erreurEcheanceAvant;
        return trouves;
    }, [r.montant, r.dateFacture, r.echeance, t]);

    const refusDe = (champ) => (controle.invalides || []).find((i) => i.champ === champ);
    const erreurDe = (champ) => {
        if (soucis[champ]) return soucis[champ];
        const refus = refusDe(champ);
        if (!refus) return "";
        if (champ === "montant") return t.erreurMontant;
        if (refus.motif === "avantLaFacture") return t.erreurEcheanceAvant;
        return t.erreurFormat;
    };

    const commun = { ...communs, phases: t.phases, retour: t.retour, rang };
    const tri = concerne(r, aujourdhui);
    const professionnel = r.client === "professionnel";

    const telecharger = () =>
        telechargerDocument({
            document: lettre,
            signature,
            nomFichier: nomDuFichier("mise-en-demeure", r),
        });

    const copier = async () => {
        if (!lettre) return;
        try {
            await navigator.clipboard.writeText(documentEnTexte(lettre));
            setCopie(true);
            window.setTimeout(() => setCopie(false), 2000);
        } catch {
            // Presse-papiers refusé : le PDF reste la voie normale.
        }
    };

    /* ---------------------------------------------------------------- */
    /* Le tri                                                            */
    /* ---------------------------------------------------------------- */

    if (etape === "client") {
        return (
            <Ecran key={etape} {...commun} phase={0} titre={t.clientTitre} aide={t.clientAide}>
                <div className="grid sm:grid-cols-2 gap-3">
                    <Choix
                        icone="buildings-2-linear"
                        titre={t.clientPro}
                        detail={t.clientProDetail}
                        onClick={() => {
                            setR((p) => ({ ...p, client: "professionnel" }));
                            aller("facture");
                        }}
                    />
                    <Choix
                        icone="user-rounded-linear"
                        titre={t.clientParticulier}
                        detail={t.clientParticulierDetail}
                        onClick={() => {
                            setR((p) => ({ ...p, client: "particulier" }));
                            aller("facture");
                        }}
                    />
                </div>
                <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-5 mb-0">{t.prive}</p>
            </Ecran>
        );
    }

    if (etape === "facture") {
        return (
            <Ecran key={etape} {...commun} phase={0} onRetour={retour} titre={t.factureTitre} aide={t.factureAide}>
                <div className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Champ libelle={t.champNumero} valeur={r.numeroFacture} poser={poser("numeroFacture")} />
                        <Champ
                            libelle={t.champMontant}
                            aide={t.aideMontant}
                            valeur={r.montant}
                            poser={poser("montant")}
                            erreur={erreurDe("montant")}
                            inputMode="decimal"
                        />
                    </div>
                    <Champ
                        libelle={t.champObjet}
                        aide={t.aideObjet}
                        valeur={r.objet}
                        poser={poser("objet")}
                    />
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Champ
                            type="date"
                            libelle={t.champDateFacture}
                            valeur={r.dateFacture}
                            poser={poser("dateFacture")}
                            erreur={erreurDe("dateFacture")}
                        />
                        <Champ
                            type="date"
                            libelle={t.champEcheance}
                            aide={t.aideEcheance}
                            valeur={r.echeance}
                            poser={poser("echeance")}
                            erreur={erreurDe("echeance")}
                        />
                    </div>
                </div>
                <Avancer
                    pleineLargeur
                    disabled={
                        !rempli("numeroFacture", "montant", "objet", "dateFacture", "echeance") ||
                        Object.keys(soucis).length > 0
                    }
                    onClick={() => aller(tri.motif === "pasEncoreEchue" ? "horsSujet" : "prestation")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    /* La date d'où court la prescription, seule sur son écran : elle ne sert qu'à
       ça, et c'est elle que tout le monde confond avec l'échéance. */
    if (etape === "prestation") {
        return (
            <Ecran key={etape} {...commun} phase={0} onRetour={retour} titre={t.prestationTitre} aide={t.prestationAide}>
                <div className="space-y-5">
                    <Champ
                        type="date"
                        libelle={t.champFinPrestation}
                        aide={t.aideFinPrestation}
                        valeur={r.finPrestation}
                        poser={poser("finPrestation")}
                    />
                </div>
                <Avancer
                    pleineLargeur
                    disabled={!rempli("finPrestation") || tri.concerne === null}
                    onClick={() => aller(tri.concerne ? (professionnel ? "taux" : "parties") : "horsSujet")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "horsSujet") {
        const texte =
            tri.motif === "pasEncoreEchue"
                ? t.refusPasEncoreEchue
                : professionnel
                  ? t.refusPrescritPro
                  : t.refusPrescritParticulier;
        return (
            <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.horsSujetTitre} phases={t.phases} sens={sens} reduit={reduit}>
                <div className="mt-4">
                    <p className="text-[15px] leading-7 text-gray-600 dark:text-lightBlue mb-0">{texte}</p>
                    <Recommencer onClick={recommencer} libelle={t.recommencer} />
                </div>
            </Resultat>
        );
    }

    if (etape === "taux") {
        const sousLePlancher =
            r.tauxPrevu === "oui" &&
            Number(String(r.tauxContrat).replace(",", ".")) > 0 &&
            Number(String(r.tauxContrat).replace(",", ".")) < TAUX_PLANCHER;
        return (
            <Ecran key={etape} {...commun} phase={1} onRetour={retour} titre={t.tauxTitre} aide={t.tauxAide}>
                <div className="space-y-5">
                    <div>
                        <Segments
                            libelle={t.champTauxPrevu}
                            options={[
                                { cle: "oui", libelle: t.oui },
                                { cle: "non", libelle: t.non },
                            ]}
                            valeur={r.tauxPrevu}
                            poser={poser("tauxPrevu")}
                        />
                    </div>
                    {r.tauxPrevu === "oui" && (
                        <Champ
                            libelle={t.champTaux}
                            aide={remplir(t.aideTaux, { plancher: pourcent(TAUX_PLANCHER) })}
                            valeur={r.tauxContrat}
                            poser={poser("tauxContrat")}
                            erreur={erreurDe("tauxContrat")}
                            inputMode="decimal"
                        />
                    )}
                    {r.tauxPrevu === "non" && (
                        <p className="text-sm leading-7 text-gray-600 dark:text-lightBlue mb-0">
                            {remplir(t.tauxSuppletif, { taux: pourcent(TAUX_SUPPLETIF) })}
                        </p>
                    )}
                    {sousLePlancher && (
                        <p className="text-sm leading-7 text-amber-600 dark:text-amber-400 mb-0">
                            {remplir(t.tauxRemonte, { plancher: pourcent(TAUX_PLANCHER) })}
                        </p>
                    )}
                </div>
                <Avancer
                    pleineLargeur
                    disabled={!r.tauxPrevu || (r.tauxPrevu === "oui" && !rempli("tauxContrat"))}
                    onClick={() => aller("parties")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "parties") {
        return (
            <Ecran key={etape} {...commun} phase={1} onRetour={retour} titre={t.partiesTitre} aide={t.partiesAide}>
                <div className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Champ libelle={t.champPrenoms} valeur={r.prenoms} poser={poser("prenoms")} />
                        <Champ libelle={t.champNom} valeur={r.nom} poser={poser("nom")} />
                    </div>
                    <Champ
                        type="textarea"
                        libelle={t.champAdresse}
                        aide={t.aideAdresse}
                        valeur={r.adresse}
                        poser={poser("adresse")}
                        rows={2}
                    />
                    <Champ
                        libelle={t.champDestinataire}
                        aide={t.aideDestinataire}
                        valeur={r.destinataire}
                        poser={poser("destinataire")}
                    />
                    <Champ
                        type="textarea"
                        libelle={t.champAdresseDestinataire}
                        valeur={r.adresseDestinataire}
                        poser={poser("adresseDestinataire")}
                        rows={2}
                    />
                </div>
                <Avancer
                    pleineLargeur
                    disabled={!rempli("prenoms", "nom", "adresse", "destinataire", "adresseDestinataire")}
                    onClick={() => aller("signature")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "signature") {
        return (
            <Ecran key={etape} {...commun} phase={2} onRetour={retour} titre={t.signatureTitre} aide={t.signatureAide}>
                <div className="space-y-5">
                    <Champ
                        libelle={t.champLieuSignature}
                        valeur={r.lieuDeSignature}
                        poser={poser("lieuDeSignature")}
                    />
                    <Champ
                        type="date"
                        libelle={t.champDateDeSignature}
                        valeur={r.dateDeSignature || jour}
                        poser={poser("dateDeSignature")}
                        erreur={erreurDe("dateDeSignature")}
                    />
                    <PadSignature
                        libelle={t.signatureDessin}
                        aide={t.signatureDessinAide}
                        effacer={t.effacer}
                        repriseLibelle={t.signatureReprise}
                        valeur={signature}
                        poser={poserSignature}
                    />
                </div>
                <Avancer pleineLargeur disabled={!rempli("lieuDeSignature")} onClick={() => aller("resultat")}>
                    {t.voirDocument}
                </Avancer>
            </Ecran>
        );
    }

    /* ---------------------------------------------------------------- */
    /* Le résultat                                                       */
    /* ---------------------------------------------------------------- */

    return (
        <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.resultatTitre} phases={t.phases} sens={sens} reduit={reduit}>
            {!lettre ? (
                <div className="mt-4">
                    <div className="font-bold text-lg dark:text-white leading-snug">
                        {controle.manques.length ? t.manquesTitre : t.verifierTitre}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                        {controle.manques.length ? t.manquesAide : t.verifierAide}
                    </p>
                    <ul className="mt-4 space-y-2">
                        {controle.manques.map((cle) => (
                            <li key={cle} className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue">
                                <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-red-500/70" />
                                <span>{LIBELLES_MANQUES[lang]?.[cle] || LIBELLES_MANQUES.fr[cle]}</span>
                            </li>
                        ))}
                        {(controle.invalides || []).map(({ champ, motif }) => (
                            <li
                                key={`${champ}-${motif}`}
                                className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue"
                            >
                                <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500/70" />
                                <span>
                                    {LIBELLES_MANQUES[lang]?.[champ] || LIBELLES_MANQUES.fr[champ]}
                                    {" : "}
                                    {erreurDe(champ)}
                                </span>
                            </li>
                        ))}
                    </ul>
                    <Avancer pleineLargeur onClick={retour}>
                        {t.retour}
                    </Avancer>
                </div>
            ) : (
                <div className="mt-4">
                    <div className="rounded-2xl border border-gray-200/70 dark:border-white/10 p-5 mb-6">
                        <div className="text-[11px] font-semibold text-gray-500 dark:text-lightBlue/70 uppercase tracking-wider mb-2">
                            {t.decompteTitre}
                        </div>
                        <div className="text-3xl font-bold dark:text-white">{euros(compte.total)}</div>
                    </div>

                    {controle.alertes.includes("prescriptionProche") && delai && (
                        <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4 mb-5">
                            <p className="text-sm text-gray-700 dark:text-lightBlue mb-0">
                                {remplir(t.alertePrescriptionProche, {
                                    ans: PRESCRIPTION_ANS[r.client],
                                    date: dateLongue(delai.limite, lang),
                                })}
                            </p>
                        </div>
                    )}
                    {controle.alertes.includes("tauxSousLePlancher") && (
                        <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4 mb-5">
                            <p className="text-sm text-gray-700 dark:text-lightBlue mb-0">
                                {remplir(t.alerteTauxSousPlancher, { plancher: pourcent(TAUX_PLANCHER) })}
                            </p>
                        </div>
                    )}

                    <div className="text-[11px] font-semibold text-gray-500 dark:text-lightBlue/70 uppercase tracking-wider mb-3">
                        {t.recapTitre}
                    </div>
                    <div className="rounded-2xl border border-gray-200/70 dark:border-white/10 overflow-hidden mb-6">
                        <table className="w-full text-sm">
                            <tbody>
                                {recap.map(([titre, valeur]) => (
                                    <tr
                                        key={titre}
                                        className="border-b last:border-b-0 border-gray-200/70 dark:border-white/10"
                                    >
                                        <td className="px-5 py-3 text-gray-500 dark:text-lightBlue/70 align-top w-2/5">
                                            {titre}
                                        </td>
                                        <td className="px-5 py-3 font-medium dark:text-white">{valeur}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {compte.interetsACourir && (
                        <div className="mb-6 rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] p-5">
                            <div className="flex items-center gap-2.5 mb-3">
                                <SolarIcon
                                    name="info-circle-linear"
                                    className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                                />
                                <div className="font-semibold dark:text-white leading-snug">
                                    {t.interetsACourirTitre}
                                </div>
                            </div>
                            <p className="text-sm leading-7 text-gray-600 dark:text-lightBlue mb-0">
                                {remplir(t.interetsACourir, { taux: pourcent(TAUX.legalCreancierPro) })}
                            </p>
                        </div>
                    )}

                    <div className="text-[11px] font-semibold text-gray-500 dark:text-lightBlue/70 uppercase tracking-wider mb-3">
                        {t.apercu}
                    </div>
                    <ApercuDocument document={lettre} signature={signature} />

                    <div className="mt-5 flex flex-col sm:flex-row gap-3">
                        <button
                            type="button"
                            onClick={telecharger}
                            className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#D04515] text-white font-semibold transition-opacity duration-200 hover:opacity-90"
                        >
                            <SolarIcon name="download-minimalistic-linear" className="text-lg" />
                            {t.telecharger}
                        </button>
                        <button
                            type="button"
                            onClick={copier}
                            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-gray-200/70 dark:border-white/10 text-gray-600 dark:text-lightBlue font-semibold transition-colors duration-200 hover:border-primary/40 hover:text-primary"
                        >
                            <SolarIcon name={copie ? "check-circle-linear" : "copy-linear"} className="text-lg" />
                            {copie ? t.copie : t.copier}
                        </button>
                    </div>

                    <div className="mt-8">
                        <div className="flex items-center gap-2.5 mb-4">
                            <SolarIcon
                                name="clipboard-check-linear"
                                className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                            />
                            <div className="font-bold dark:text-white leading-snug">{t.suiteTitre}</div>
                        </div>
                        <Liste
                            items={[
                                remplir(t.suite1, { delai: DELAI_MISE_EN_DEMEURE_JOURS }),
                                t.suite2,
                                t.suite3,
                                t.suite4,
                            ]}
                        />
                    </div>

                    {professionnel && (
                        <div className="mt-8 rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] p-5">
                            <div className="flex items-center gap-2.5 mb-3">
                                <SolarIcon
                                    name="hand-money-linear"
                                    className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                                />
                                <div className="font-semibold dark:text-white leading-snug">{t.fondTitre}</div>
                            </div>
                            <p className="text-sm leading-7 text-gray-600 dark:text-lightBlue mb-0">
                                {t.fond}
                            </p>
                        </div>
                    )}

                    <Recommencer onClick={recommencer} libelle={t.recommencer} />
                </div>
            )}
        </Resultat>
    );
}
